/**
 * Task 2.0 API client.
 *
 * Consumes apps/web/kakehashi/api/src/routes/tasks.ts at /api/v1.
 * In dev, vite proxies /api → :8787 (see vite.config.ts).
 *
 * Kept zero-dependency (no axios/react-query) so the feature compiles
 * without picking a stack the rest of the app may not yet have settled on.
 */
import { authenticatedFetch, resolveApiUrl } from "@/lib/api";
import type {
  CreateTaskInput,
  DependencyType,
  GanttView,
  KanbanView,
  Task,
  TaskComment,
  TaskDependency,
  TaskStatus,
  UpdateTaskInput,
} from "./types";

export const API_BASE = resolveApiUrl("/v1");

export class TaskApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "TaskApiError";
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  fetchImpl: typeof fetch = fetch,
): Promise<T> {
  const headers: Record<string, string> = {
    accept: "application/json",
    "x-aile-task-execution": "ca_phone,ca_case,x_reply,video_upload",
    ...((init.headers as Record<string, string>) ?? {}),
  };
  if (init.body && !headers["content-type"]) {
    headers["content-type"] = "application/json";
  }
  const res = await fetchImpl(`${API_BASE}${path}`, { ...init, headers });
  if (res.status === 204) {
    return undefined as T;
  }
  const text = await res.text();
  const json = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const code = json?.error?.code ?? "http_error";
    const message = json?.error?.message ?? `HTTP ${res.status}`;
    throw new TaskApiError(res.status, code, message);
  }
  return json as T;
}

export interface ListTasksParams {
  workspace_id?: string;
  room_id?: string;
  status?: TaskStatus[];
  assignee?: string;
  project_id?: string;
  due_before?: number;
  due_after?: number;
  tag?: string;
  search?: string;
  parent_task_id?: string | null;
  include_archived?: boolean;
}

function toQuery(params: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    if (Array.isArray(v)) {
      if (v.length === 0) continue;
      usp.set(k, v.join(","));
    } else if (typeof v === "boolean") {
      if (v) usp.set(k, "true");
    } else {
      usp.set(k, String(v));
    }
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

export interface TaskApiOptions {
  fetchImpl?: typeof fetch;
}

export class TaskApi {
  private readonly fetchImpl: typeof fetch;
  constructor(opts: TaskApiOptions = {}) {
    this.fetchImpl = opts.fetchImpl ?? authenticatedFetch;
  }

  syncExecution(id: string, workspaceId: string): Promise<{ task: Task }> {
    return request(`/tasks/${encodeURIComponent(id)}/execution-sync`, {
      method: "POST", body: JSON.stringify({ workspace_id: workspaceId }),
    }, this.fetchImpl);
  }

  listComments(id: string, workspaceId: string): Promise<{ comments: TaskComment[] }> {
    return request(`/tasks/${encodeURIComponent(id)}/comments${toQuery({ workspace_id: workspaceId })}`, {}, this.fetchImpl);
  }

  addComment(id: string, workspaceId: string, body: string, sourceId: string): Promise<{ comment: TaskComment }> {
    return request(`/tasks/${encodeURIComponent(id)}/comments`, {
      method: "POST", body: JSON.stringify({ workspace_id: workspaceId, body_md: body, source_id: sourceId }),
    }, this.fetchImpl);
  }

  listTasks(params: ListTasksParams = {}): Promise<{ tasks: Task[] }> {
    const query: Record<string, unknown> = { ...params };
    if (params.parent_task_id === null) {
      query.parent_task_id = "null";
    }
    return request(`/tasks${toQuery(query)}`, {}, this.fetchImpl);
  }

  getTask(id: string): Promise<{
    task: Task;
    dependencies: { blocked_by: TaskDependency[]; blocks: TaskDependency[] };
    subtask_progress: { total: number; done: number; ratio: number };
  }> {
    return request(`/tasks/${encodeURIComponent(id)}`, {}, this.fetchImpl);
  }

  createTask(input: CreateTaskInput): Promise<{ task: Task }> {
    return request(
      "/tasks",
      { method: "POST", body: JSON.stringify(input) },
      this.fetchImpl,
    );
  }

  updateTask(id: string, patch: UpdateTaskInput): Promise<{ task: Task }> {
    return request(
      `/tasks/${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify(patch) },
      this.fetchImpl,
    );
  }

  deleteTask(id: string): Promise<void> {
    return request(
      `/tasks/${encodeURIComponent(id)}`,
      { method: "DELETE" },
      this.fetchImpl,
    );
  }

  createSubtask(parentId: string, input: CreateTaskInput): Promise<{ task: Task }> {
    return request(
      `/tasks/${encodeURIComponent(parentId)}/subtasks`,
      { method: "POST", body: JSON.stringify(input) },
      this.fetchImpl,
    );
  }

  listSubtasks(parentId: string): Promise<{
    subtasks: Task[];
    progress: { total: number; done: number; ratio: number };
  }> {
    return request(
      `/tasks/${encodeURIComponent(parentId)}/subtasks`,
      {},
      this.fetchImpl,
    );
  }

  addDependency(
    successorId: string,
    predecessorId: string,
    type: DependencyType = "FS",
  ): Promise<{ dependency: TaskDependency }> {
    return request(
      `/tasks/${encodeURIComponent(successorId)}/dependencies`,
      {
        method: "POST",
        body: JSON.stringify({ predecessor_id: predecessorId, type }),
      },
      this.fetchImpl,
    );
  }

  removeDependency(successorId: string, predecessorId: string): Promise<void> {
    return request(
      `/tasks/${encodeURIComponent(successorId)}/dependencies/${encodeURIComponent(predecessorId)}`,
      { method: "DELETE" },
      this.fetchImpl,
    );
  }

  kanban(params: {
    workspace_id?: string;
    room_id?: string;
    project_id?: string;
  } = {}): Promise<KanbanView> {
    return request(`/tasks/kanban${toQuery(params)}`, {}, this.fetchImpl);
  }

  gantt(params: {
    workspace_id?: string;
    room_id?: string;
    project_id?: string;
    from?: number;
    to?: number;
  } = {}): Promise<GanttView> {
    return request(`/tasks/gantt${toQuery(params)}`, {}, this.fetchImpl);
  }

  promoteFromMessage(input: {
    message_id: string;
    title: string;
    description?: string;
    room_id?: string;
    assignee?: string;
    due_at?: number | null;
  }): Promise<{ task: Task }> {
    return request(
      "/tasks/promote-from-message",
      { method: "POST", body: JSON.stringify(input) },
      this.fetchImpl,
    );
  }

  suggestDue(taskId: string): Promise<{
    suggested_due_at: number;
    rationale: string;
    provider: string;
  }> {
    return request(
      `/tasks/${encodeURIComponent(taskId)}/ai-suggest-due`,
      { method: "POST" },
      this.fetchImpl,
    );
  }
}

export const taskApi = new TaskApi();
